import {
    FormEventHandler,
    ReactElement,
    useCallback,
    useEffect,
    useState
} from 'react';
import { useLoginAction } from '../actions';
import { UIButton, UIFormInput, UIInlineAlert, UISpacer } from '@/ui';
import { SsoSignIn } from './sso-sign-in';
import styles from './login.module.scss';
import { validateEmail } from '@/common/util/validators/email';
import { useMeTranslation } from '../hooks';

export type LoginProps = {
    onSuccess?: () => void;
};

export function Login(props: LoginProps): ReactElement {
    const { onSuccess } = props;

    const { login, loading, error, clearError } = useLoginAction();

    return (
        <>
            {error && (
                <>
                    <UIInlineAlert text={error} onClose={clearError} />
                    <UISpacer multiplier={2} />
                </>
            )}
            <LoginForm
                loading={loading}
                onSubmit={(data) => {
                    login({ email: data.email, password: data.password })
                        .then(() => {
                            onSuccess?.();
                        })
                        .catch(() => {}); // Handled in the hook
                }}
            />

            <SsoSignIn disabled={loading} onSuccess={onSuccess} />
        </>
    );
}

type LoginFormProps = {
    onSubmit?: (data: { email: string; password: string }) => void;
    loading?: boolean;
};
export function LoginForm(props: LoginFormProps): ReactElement {
    const { t } = useMeTranslation();

    const { onSubmit, loading } = props;
    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Email
     */
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const checkEmail = useCallback(
        (email: string): string => {
            const error = validateEmail(email);
            if (error) {
                return t(error);
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setEmailError(checkEmail(email));
        }
    }, [email, showRealTimeErrors, checkEmail]);

    /**
     * Password
     */
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const checkPassword = useCallback(
        (password: string): string => {
            if (password.length === 0) {
                return t('shared.fields.password.error_required');
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setPasswordError(checkPassword(password));
        }
    }, [password, showRealTimeErrors, checkPassword]);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkEmail(email) && !checkPassword(password)) {
            setShowRealTimeErrors(false);
            onSubmit?.({ email, password });
        }
    };

    return (
        <div className={styles.loginForm}>
            <form onSubmit={handleSubmit} noValidate>
                <UIFormInput
                    name="email"
                    label={t('shared.fields.email.label')}
                    value={email}
                    type="email"
                    autoComplete="on"
                    errorText={emailError}
                    autoFocus={true}
                    required
                    maxLength={320}
                    onChange={setEmail}
                />
                <UISpacer multiplier={2} />
                <UIFormInput
                    name="password"
                    label={t('shared.fields.password.label')}
                    labelButton={{
                        text: t('components.login.forgot_password'),
                        link: '/forgot-password'
                    }}
                    value={password}
                    type="password"
                    autoComplete="on"
                    errorText={passwordError}
                    required
                    maxLength={128}
                    onChange={setPassword}
                />
                <UISpacer multiplier={2} />
                <UIButton
                    variant="primary"
                    type="submit"
                    loading={loading}
                    disabled={loading}
                    fullWidth
                >
                    {t('shared.labels.login')}
                </UIButton>
            </form>
        </div>
    );
}
